import React, { memo, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
	currentStepSelector,
	formSummarySelector,
	receiverInformationStepSelector
} from '../../../store/modules/steps/selectors';
import { CustomSelectType, CustomSelectTypeOptions, FormStep, FormStepProps, StepsState } from '../../../model/global';
import CustomFormField from '../../FormControls/CustomFormField';
import { getFormControlInitialState, getFormSummaryField } from '../../utils';
import { CustomFormFieldState } from '../../FormControls/CustomFormField/CustomFormField';
import { stepsActions } from '../../../store/modules/steps/reducer';
import {Box} from "@material-ui/core";
import Button from "../../../../../../../../global/react-mui/atoms/Button";
import {translationsSelector} from "../../../store/modules/translations/selectors";

const ReceiverInformation: React.FC<FormStepProps> = ({ onStepControlsChange, data: { stepTemplate } }) => {

	const dispatch = useDispatch()
	const step = useSelector(currentStepSelector) as FormStep<'commissionData'>
	const summary = useSelector(formSummarySelector) as StepsState['formSummary']
	const stepErrors = summary.validationErrors[step.stepTemplate];
	const {copyValuesFromPreviousStepButtonText} = useSelector(translationsSelector)


	// initialise the step data once
	useEffect(() => {
		// don't re-init fields
		if (summary.initialized[stepTemplate]) return;

		const initialData: Record<string, CustomFormFieldState> = {};



		const partners = summary.steps.MAIN_ENQUIRY.target_group?.originalField['options']
		const requiredPartners: Array<string> = []
		if (partners) {
			partners.forEach((partner: CustomSelectTypeOptions) => {
				if (partner.options && partner.options[0].label === 'companyFieldRequired' && partner.options[0].value === 'true') {
					if (typeof partner.value === "string") {
						requiredPartners.push(partner.value)
					}
				}
			})
		}


		// build commission address fields
		step.stepData.fields.commissionData.forEach(element => {

			if (!element || !element.name) return;
			const item = getFormControlInitialState(element.name, step.stepData.fields.commissionData);
			if (item && item.name === 'commission_company') {
				item.required = !!(summary.steps.MAIN_ENQUIRY.target_group && requiredPartners.includes(summary.steps.MAIN_ENQUIRY.target_group.value));
			}

			const value = summary.steps.RECEIVER_INFORMATION
			if (item && value[item.name] && value[item.name]?.value) {
				item.value = value[item.name]?.value
			}

			if (item) initialData[element.name] = item
		})

		dispatch(stepsActions.initializeStepPayloadValues({
			stepTemplate,
			stepPayload: initialData,
			initialize: true,
		}));

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		dispatch,
		step.stepData.fields.commissionData,
		stepTemplate,
		summary.initialized,
		summary.steps.MAIN_ENQUIRY.target_group
	])
	const fieldChangeHandler = (event: CustomFormFieldState): void => {
		dispatch(stepsActions.updateStepPayloadValues({
			stepTemplate,
			stepPayload: event,
		}));
	}

	if (!step) return null

	const attributeOBJ = (field: any) => { return ({ field, fieldErrors: stepErrors }) }



	const commissionFields: CustomFormFieldState<any>[] = []
	step.stepData.fields.commissionData.forEach(f => {
		if (!f.name) return;
		if (Object.keys(summary.steps[stepTemplate]).length === 0) return;
		const item = getFormSummaryField(summary, stepTemplate, f.name);
		item && commissionFields.push({ ...item, required: item?.originalField?.required || false })
	})

	const handleCopyValuesFromCustomerInformation = () => {


		// if the field is checked, populate the 2nd or 3rd form
		// checkbox1 is connected to commissionFields (step.stepData.fields.commissionData)


		const allRelated: CustomFormFieldState[] = [];

		step.stepData.fields.commissionData.forEach(f => {
			const reduxItem = getFormSummaryField(summary, stepTemplate, f.name);
			if (!reduxItem || !reduxItem.originalField.relatedField) return;
			// put the corresponding main address value in the commission address field
			const {relatedField} = reduxItem.originalField
			// get the related field data from the address
			const relatedAddressFormControl = getFormSummaryField(summary, 'CUSTOMER_INFORMATION', relatedField);
			if (!relatedAddressFormControl) return;

			// only copy values which are valid
			if (relatedAddressFormControl.valid) {
				const newRelatedItem = JSON.parse(JSON.stringify(reduxItem)) as CustomFormFieldState;
				newRelatedItem.value = relatedAddressFormControl.value
				newRelatedItem.valid = relatedAddressFormControl.valid // also update validity?
				allRelated.push(newRelatedItem)
			}
		})
		dispatch(stepsActions.updateStepPayloadValues({
			stepTemplate,
			stepPayload: allRelated,
		}));

	}

	return (
		<div className='uk-grid-medium uk-grid uk-grid-stack'>

			<Box>
				<fieldset className={'customer-address-fieldset'}>
					<h2 className={'customer-address-fieldset-title'}>{step.stepData.fieldTranslations.commissionData}</h2>
					<div className={'uk-width-1-1 accordion-space'}>
						<Button {
									...{
										onClick: () => handleCopyValuesFromCustomerInformation(),
										variant: "outlined",
										size: "large",
										className: "btn-step btn-prev"
									}
								}>
							{copyValuesFromPreviousStepButtonText}
						</Button>
					</div>

					<div className="uk-grid-medium uk-grid uk-grid-stack">
						{commissionFields?.map((field, key) => {
							return <CustomFormField key={`comission-${key}`} {...attributeOBJ(field)} onFieldChange={e => fieldChangeHandler(e)} />
						})}
					</div>
				</fieldset>
			</Box>
		</div>
	);
};

export default memo(ReceiverInformation);
